import type { Testimonial } from '@/types/api'

interface Props {
  testimonials: Testimonial[]
}

/**
 * Continuously scrolling marquee of testimonial cards (left → right).
 * Duplicates the list to create a seamless loop. Pauses on hover.
 * CSS-driven so it's smooth even under Framer-heavy pages.
 */
export function TestimonialsMarquee({ testimonials }: Props) {
  if (!testimonials.length) return null
  const doubled = [...testimonials, ...testimonials]
  return (
    <div className="relative overflow-hidden py-2 [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
      <div className="marquee-track flex gap-6 w-max animate-[marquee_45s_linear_infinite] hover:[animation-play-state:paused]">
        {doubled.map((t, i) => (
          <TestimonialCard key={i} t={t} />
        ))}
      </div>
      <style>{`
        @keyframes marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .marquee-track { animation: none !important; }
        }
      `}</style>
    </div>
  )
}

function TestimonialCard({ t }: { t: Testimonial }) {
  return (
    <blockquote className="w-[380px] md:w-[440px] shrink-0 rounded-2xl border border-border bg-card p-6 flex flex-col gap-3 shadow-sm">
      <div className="text-cosmos-gold font-display text-5xl leading-none">&ldquo;</div>
      <p className="text-sm md:text-[15px] leading-relaxed text-foreground/85 flex-1">{t.quote}</p>
      <footer className="mt-2 pt-3 border-t border-dashed border-border">
        <div className="font-display uppercase text-base tracking-wide">{t.author}</div>
        <div className="text-xs text-muted-foreground">{t.role}</div>
      </footer>
    </blockquote>
  )
}
